import * as R from 'ramda';
import { getCrossProjectVersionsById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/cross-project-versions';
import { getFilters } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters';
import { getAssigneeForFilter } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/assignee-filter';
import { COMPONENT_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/component-filter';
import { CROSS_PROJECT_RELEASE_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/cross-project-release-filter';
import { getCustomFieldFilterCount } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/custom-field-filter';
import { DEPENDENCIES_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/dependencies-filter';
import { ISSUE_PRIORITIES_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/issue-priorities-filter';
import { ISSUE_TYPE_KEY_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/issue-type-key-filter';
import {
	LABEL_FILTER_ID,
	NO_LABEL_ID,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/label-filter';
import { PROJECT_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/project-filter';
import {
	RELEASE_FILTER_ID,
	NO_RELEASE_ID,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/release-filter';
import { getReporterForFilter } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/reporter-filter';
import { NO_SPRINT_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/sprint-filter';
import { getIssueStatusFilterOptions } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/status-filter';
import { STATUS_KEY_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/status-key-filter';

import { SUMMARY_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/summary-filter';
import { WARNING_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/warning-filter';
import { getIssueTypeWithLevelById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/hierarchy';
import { getIssueGoalARIs } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-goals';
import { getIssueLabels } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-labels';
import { getIssuePrioritiesById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-priorities';
import {
	getIssueTypesById,
	getIssueTypeIconByKey,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-types';
import { getIssueMapById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues';
import {
	getProjects,
	getProjectsById,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/projects';
import {
	getAllSprintsByIdMap,
	getExternalSprintsById,
	getSprintStatesCategories,
	getUniqueFilterSprints,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints';
import { isAtlasConnectInstalled } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/system';
import { getTeamsById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/teams';
import { getVersionsById } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/versions';
import type { Person } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/assignees/types';
import type { IssueTypeWithLevel } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/hierarchy/types';
import type { IssueStatus } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/issue-statuses/types';
import type { Project } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/projects/types';
import type { Person as ReporterPerson } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/reporters/types';
import {
	TEAM_FILTER_ID,
	ASSIGNEE_FILTER_ID,
	REPORTER_FILTER_ID,
	GOAL_FILTER_ID,
	DATE_RANGE_FILTER_ID,
	ISSUE_WARNING_FILTER_ID,
	type DateRangeFilterValue,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { sortAsLowerCaseByNameProp } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda/composed';
import { createSelector } from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect';
import {
	CHECKBOX_STATES,
	EXTERNAL_SPRINT,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import type { FilterInfo, SprintFilterInfo, QueryProps } from './types';

export const { CHECKED, UNCHECKED, INDETERMINATE } = CHECKBOX_STATES;

export const getFilteredReleasesPure = (
	{ [RELEASE_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	versionsById: ReturnType<typeof getVersionsById>,
) => {
	const versionNames = R.map(
		(releaseId) => ({
			id: releaseId,
			name:
				releaseId !== NO_RELEASE_ID && isDefined(versionsById[releaseId])
					? versionsById[releaseId].name
					: '',
		}),
		value,
	);
	return isDefined(versionNames) ? sortAsLowerCaseByNameProp(versionNames) : versionNames;
};

export const getFilteredCrossProjectReleasesPure = (
	{
		[CROSS_PROJECT_RELEASE_FILTER_ID]: { value: crossProjectReleaseFilterValue = [] },
	}: ReturnType<typeof getFilters>,
	crossProjectVersionsById: ReturnType<typeof getCrossProjectVersionsById>,
) => {
	const crossProjectVersionNames = sortAsLowerCaseByNameProp(
		R.map(
			(releaseId) => ({
				id: releaseId,
				name: isDefined(crossProjectVersionsById[releaseId])
					? crossProjectVersionsById[releaseId].name
					: '',
			}),
			crossProjectReleaseFilterValue,
		),
	);
	return isDefined(crossProjectVersionNames)
		? sortAsLowerCaseByNameProp(crossProjectVersionNames)
		: crossProjectVersionNames;
};

export const getFilteredTeamsPure = (
	{ [TEAM_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	teamsById: ReturnType<typeof getTeamsById>,
) => {
	const filteredTeams = R.map(
		(teamId) => ({
			id: teamId,
			name: isDefined(teamsById[teamId]) ? teamsById[teamId].title : '',
		}),
		value,
	);

	return isDefined(filteredTeams) ? sortAsLowerCaseByNameProp(filteredTeams) : filteredTeams;
};

export const getFilteredIssueTypeKeyPure = (
	{ [ISSUE_TYPE_KEY_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	issueTypeWithLevelById: Record<number, IssueTypeWithLevel>,
	issueTypeIconByKey: Record<string, string>,
): FilterInfo[] =>
	value.reduce<FilterInfo[]>((acc, issueTypeId) => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const issueTypeWithLevel = issueTypeWithLevelById[issueTypeId as number];
		if (isDefined(issueTypeWithLevel)) {
			acc.push({
				id: issueTypeId,
				name: issueTypeWithLevel.name,
				iconUrl: issueTypeIconByKey[`${issueTypeWithLevel.level} ${issueTypeWithLevel.name}`],
				// using key to store the hierarchy level
				key: `${issueTypeWithLevel.level}`,
			});
		}
		return acc;
	}, []);

export const getFilteredIssuePrioritiesPure = (
	{ [ISSUE_PRIORITIES_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	issuePrioritiesById: ReturnType<typeof getIssuePrioritiesById>,
) =>
	R.map(
		(issuePriorityId) => ({
			id: issuePriorityId,
			name: isDefined(issuePrioritiesById[issuePriorityId])
				? issuePrioritiesById[issuePriorityId].name
				: '',
		}),
		value,
	);

export const getFilteredAssigneesPure = (
	{ [ASSIGNEE_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	assignees: ReturnType<typeof getAssigneeForFilter>,
) =>
	R.map<Person, { id: Person['personId']; name: string }>(
		({ personId, jiraUser }) => ({ id: personId, name: jiraUser ? jiraUser.title : '' }),
		R.filter(({ personId }) => R.contains(personId, value), assignees),
	);

export const getFilteredReportersPure = (
	{ [REPORTER_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	reporters: ReturnType<typeof getReporterForFilter>,
) =>
	R.map<ReporterPerson, { id: ReporterPerson['personId']; name: string }>(
		({ personId, jiraUser }) => ({ id: personId, name: jiraUser ? jiraUser.title : '' }),
		R.filter(({ personId }) => R.contains(personId, value), reporters),
	);

export const getAdditionalFilteredSprintsPure = (
	allSprintsByIdMap: ReturnType<typeof getAllSprintsByIdMap>,
	externalSprintsById: ReturnType<typeof getExternalSprintsById>,
	filterSprints: ReturnType<typeof getUniqueFilterSprints>,
	sprintStatesCategories: ReturnType<typeof getSprintStatesCategories>,
) => {
	const results: Array<SprintFilterInfo> = [];
	if (isDefined(filterSprints)) {
		filterSprints.forEach((sprintId) => {
			if (sprintId !== NO_SPRINT_ID) {
				let filterSprintState = isDefined(allSprintsByIdMap[sprintId])
					? allSprintsByIdMap[sprintId].state
					: '';
				filterSprintState = isDefined(externalSprintsById[sprintId])
					? EXTERNAL_SPRINT
					: filterSprintState;
				let sprintName = isDefined(allSprintsByIdMap[sprintId])
					? allSprintsByIdMap[sprintId].title
					: '';
				sprintName = isDefined(externalSprintsById[sprintId])
					? externalSprintsById[sprintId].title
					: sprintName;

				const selectedSprintStateCategory = R.filter(
					({ checkboxState, sprintState }) =>
						checkboxState === CHECKED && sprintState === filterSprintState,
					sprintStatesCategories,
				);

				if (!isDefined(selectedSprintStateCategory) || selectedSprintStateCategory.length === 0) {
					results.push({
						id: sprintId,
						sprintState: filterSprintState,
						sprintName,
					});
				}
			}
		});
	}

	return results;
};
export const getFilteredSprintCategoriesPure = (
	filterSprints: ReturnType<typeof getUniqueFilterSprints>,
	sprintStatesCategories: ReturnType<typeof getSprintStatesCategories>,
) => {
	const results: Array<SprintFilterInfo> = [];
	if (isDefined(filterSprints) && filterSprints.has(NO_SPRINT_ID)) {
		results.push({
			id: NO_SPRINT_ID,
		});
	}
	sprintStatesCategories.forEach((category) => {
		if (category.checkboxState === CHECKED) {
			results.push({ id: category.sprintState, sprintState: category.sprintState });
		}
	});
	return results;
};

export const getSummaryFilterPure = ({
	[SUMMARY_FILTER_ID]: { value },
}: ReturnType<typeof getFilters>) => value || '';

export const getFilteredIssueGoalsPure = (
	{ [GOAL_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	issueGoalARIs: string[],
) => {
	const results: Array<FilterInfo> = [];

	value.forEach((goal, i) => {
		if (goal === NO_LABEL_ID) {
			results.push({
				id: NO_LABEL_ID,
				name: '',
			});
		} else if (issueGoalARIs.includes(goal)) {
			results.push({
				id: i,
				name: goal,
			});
		}
	});
	return results;
};

export const getFilteredIssueLabelsPure = (
	{ [LABEL_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	labels: ReturnType<typeof getIssueLabels>,
) => {
	const results: Array<FilterInfo> = [];
	value.forEach((label, i) => {
		if (label === NO_LABEL_ID) {
			results.push({
				id: NO_LABEL_ID,
				name: '',
			});
		} else if (labels.includes(label)) {
			results.push({
				id: i,
				name: label,
			});
		}
	});
	return results;
};

export const getFilteredStatusKeyPure = (
	{ [STATUS_KEY_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	statuses: ReturnType<typeof getIssueStatusFilterOptions>,
): IssueStatus[] => statuses.filter(({ id }) => value.indexOf(id) > -1);

export const getFilteredProjectsPure = (
	{ [PROJECT_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	projects: ReturnType<typeof getProjects>,
) => {
	const sortedProjects = R.sortBy(R.compose(R.toLower, R.prop('name')))(projects);
	return R.map<
		Project,
		{
			id: Project['id'];
			name: Project['name'];
			iconUrl: Project['avatarUrl'];
			key: Project['key'];
		}
	>(
		({ id, name, avatarUrl, key }) => ({
			id,
			name,
			iconUrl: avatarUrl,
			key,
		}),
		R.filter(({ id }) => R.contains(id, value), sortedProjects),
	);
};

export const getFilteredComponentsPure = (
	{ [COMPONENT_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	projects: ReturnType<typeof getProjects>,
	projectsById: ReturnType<typeof getProjectsById>,
) =>
	R.chain(({ components }) => components || [], projects)
		.filter(({ id }) => value.includes(id))
		.map(({ id, name, projectId }) => ({
			id,
			name,
			key: projectsById[projectId].key,
		}));

export const getFilterIssuesWithWarningSettingPure = ({
	[WARNING_FILTER_ID]: { value },
}: ReturnType<typeof getFilters>) => value || false;

export const getFilteredDependenciesPure = (
	{ [DEPENDENCIES_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	issueMapById: ReturnType<typeof getIssueMapById>,
	projectsById: ReturnType<typeof getProjectsById>,
	issueTypesById: ReturnType<typeof getIssueTypesById>,
) => {
	if (value.type === 'specificIssue') {
		const issue = isDefined(value.issueId) ? issueMapById[value.issueId] : null;
		const project = isDefined(issue) ? projectsById[issue.project] : null;
		let issueKey = '';
		if (project) {
			if (isDefined(issue) && isDefined(issue.issueKey)) {
				issueKey = `${project.key}-${issue.issueKey}`;
			} else {
				issueKey = project.key;
			}
		}
		const issueType = isDefined(issue) ? issueTypesById[issue.type] : null;
		return {
			type: value.type,
			issueKey,
			issueSummary: isDefined(issue) ? issue.summary : '',
			issueTypeIconUrl: isDefined(issueType) ? issueType.iconUrl : '',
			includeTransitive: value.includeTransitive,
		};
	}
	return {
		type: isDefined(value) ? value.type : 'off',
	};
};

const getFilteredReleases = createSelector([getFilters, getVersionsById], getFilteredReleasesPure);

const getFilteredCrossProjectReleases = createSelector(
	[getFilters, getCrossProjectVersionsById],
	getFilteredCrossProjectReleasesPure,
);

const getFilteredTeams = createSelector([getFilters, getTeamsById], getFilteredTeamsPure);

const getSummaryFilter = createSelector([getFilters], getSummaryFilterPure);

const getFilteredIssueLabels = createSelector(
	[getFilters, getIssueLabels],
	getFilteredIssueLabelsPure,
);

const getFilteredStatusKey = createSelector(
	[getFilters, getIssueStatusFilterOptions],
	getFilteredStatusKeyPure,
);

const getFilteredIssueTypeKey = createSelector(
	[getFilters, getIssueTypeWithLevelById, getIssueTypeIconByKey],
	getFilteredIssueTypeKeyPure,
);

const getFilteredIssuePriorities = createSelector(
	[getFilters, getIssuePrioritiesById],
	getFilteredIssuePrioritiesPure,
);

const getFilteredAssignees = createSelector(
	[getFilters, getAssigneeForFilter],
	getFilteredAssigneesPure,
);

const getFilteredGoals = createSelector([getFilters, getIssueGoalARIs], getFilteredIssueGoalsPure);

const getFilteredReporters = createSelector(
	[getFilters, getReporterForFilter],
	getFilteredReportersPure,
);

const getFilteredProjects = createSelector([getFilters, getProjects], getFilteredProjectsPure);

const getFilteredComponents = createSelector(
	[getFilters, getProjects, getProjectsById],
	getFilteredComponentsPure,
);

const getAdditionalFilteredSprints = createSelector(
	[getAllSprintsByIdMap, getExternalSprintsById, getUniqueFilterSprints, getSprintStatesCategories],
	getAdditionalFilteredSprintsPure,
);

const getFilteredSprintCategories = createSelector(
	[getUniqueFilterSprints, getSprintStatesCategories],
	getFilteredSprintCategoriesPure,
);

const getFilteredDependencies = createSelector(
	[getFilters, getIssueMapById, getProjectsById, getIssueTypesById],
	getFilteredDependenciesPure,
);

const getFilterIssuesWithWarningsSetting = createSelector(
	[getFilters],
	getFilterIssuesWithWarningSettingPure,
);

export const getFilteredDateRangePure = ({
	[DATE_RANGE_FILTER_ID]: { value },
}: ReturnType<typeof getFilters>): DateRangeFilterValue | undefined =>
	value.startDate || value.endDate ? value : undefined;

const getFilteredDateRange = createSelector([getFilters], getFilteredDateRangePure);

export const getFilteredIssueWarningsPure = ({
	[ISSUE_WARNING_FILTER_ID]: { value },
}: ReturnType<typeof getFilters>): string[] => value;

const getFilteredIssueWarnings = createSelector([getFilters], getFilteredIssueWarningsPure);

export const mapStateToProps = (state: State): QueryProps => ({
	releases: getFilteredReleases(state),
	crossProjectReleases: getFilteredCrossProjectReleases(state),
	teams: getFilteredTeams(state),
	summary: getSummaryFilter(state),
	issueTypeKeys: getFilteredIssueTypeKey(state),
	issuePriorities: getFilteredIssuePriorities(state),
	assignees: getFilteredAssignees(state),
	reporters: getFilteredReporters(state),
	goals: getFilteredGoals(state),
	additionalSprints: getAdditionalFilteredSprints(state),
	sprintCategories: getFilteredSprintCategories(state),
	warnings: getFilterIssuesWithWarningsSetting(state),
	labels: getFilteredIssueLabels(state),
	statusKeys: getFilteredStatusKey(state),
	projects: getFilteredProjects(state),
	components: getFilteredComponents(state),
	dependencies: getFilteredDependencies(state),
	customFieldFilterCount: getCustomFieldFilterCount(state),
	isAtlasConnectInstalled: isAtlasConnectInstalled(state),
	dateRange: getFilteredDateRange(state),
	issueWarnings: getFilteredIssueWarnings(state),
});
